import {
    setIn,
    getIn,
    FormikValues,
    FormikErrors,
    FormikTouched,
    FormikProps
} from 'formik';

// ── Types ────────────────────────────────────────────────────────────────────

export interface Step {
    label: string;
    fields: string[];
    required: boolean;
    skipIf?: (values: FormikValues, initialValues: FormikValues) => boolean;
}

export const onPageHide = (
    formik: { resetForm: () => void },
    setActiveStep: (step: number) => void,
    setVisible: (visible: boolean) => void
) => {
    formik.resetForm();
    setActiveStep(0);
    setVisible(false);
};

// ── Step navigation helpers ──────────────────────────────────────────────────


const shouldSkipStep = (
    step: Step,
    formikValues: FormikValues,
    initialValues: FormikValues,
): boolean => step.skipIf?.(formikValues, initialValues) ?? false;


export const getNextStep = (
    currentStep: number,
    steps: Step[],
    formikValues: FormikValues,
    initialValues: FormikValues,
): number => {
    let next = currentStep + 1;
    while (next < steps.length) {
        if (!shouldSkipStep(steps[next], formikValues, initialValues)) break;
        next++;
    }
    return next;
};

export const handleNextStep = async (
    currentStep: number,
    steps: Step[],
    isSkipping: boolean,
    setActiveStep: (step: number) => void,
    formik: FormikProps<any>,
) => {
    const nextStep = getNextStep(currentStep, steps, formik.values, formik.initialValues);

    if (isSkipping) {
        setActiveStep(nextStep);
        return;
    }

    const touchedFields = steps[currentStep].fields.reduce(
        (acc, field) => setIn(acc, field, true),
        {}
    );
    await formik.setTouched({ ...formik.touched, ...touchedFields }, true);

    const errors = await formik.validateForm();

    const stepHasErrors = steps[currentStep].fields.some((field) => {
        const keys = field.split('.');
        const getNestedError = (obj: any, keys: string[]): any =>
            keys.reduce((acc, key) => acc?.[key], obj);
        return !!getNestedError(errors, keys);
    });

    if (!stepHasErrors) {
        setActiveStep(nextStep);
    }
};

const getInitialValueForField = (
    field: string,
    initialValues: FormikValues,
) => {
    const keys = field.split('.');
    return keys.reduce((acc: any, key) => acc?.[key], initialValues);
};

export const getPreviousStep = (
    currentStep: number,
    steps: Step[],
    formikValues: FormikValues,
    initialValues: FormikValues,
): number => {
    let prev = currentStep - 1;
    while (prev > 0) {
        if (!shouldSkipStep(steps[prev], formikValues, initialValues)) break;
        prev--;
    }
    return prev;
};

export const handlePreviousStep = async (
    currentStep: number,
    steps: Step[],
    setActiveStep: (step: number) => void,
    formik: FormikProps<any>,
) => {
    const prevStep = getPreviousStep(currentStep, steps, formik.values, formik.initialValues);

    const resetValues = steps[currentStep].fields.reduce((acc, field) => {
        const keys = field.split('.');
        const nested = keys.reduceRight(
            (val, key) => ({ [key]: val }),
            getInitialValueForField(field, formik.initialValues) as any
        );
        return deepMerge(acc, nested);
    }, {} as any);

    await formik.setValues(deepMerge(formik.values, resetValues));
    setActiveStep(prevStep);
};

export const canSubmit = (
    initialValues: FormikValues,
    currentValues: FormikValues,
    isValid
) => {
    const valuesChanged =
        JSON.stringify(initialValues) !== JSON.stringify(currentValues);
    return valuesChanged && isValid;
};


// ── Field validation helpers ─────────────────────────────────────────────────

export const isFormFieldInvalid = (
    name: string,
    touched: FormikTouched<FormikValues>,
    errors: FormikErrors<FormikValues>
): boolean => !!(getIn(touched, name) && getIn(errors, name));

export const getFormErrorMessage = (
    name: string,
    touched: FormikTouched<FormikValues>,
    errors: FormikErrors<FormikValues>
): string | null =>
    isFormFieldInvalid(name, touched, errors)
        ? (getIn(errors, name) as string)
        : null;

// ── Nested object helpers ────────────────────────────────────────────────────

/** Reads a dot-notation path from any nested object. */
export const getNestedValue = (obj: any, path: string): any =>
    path.split('.').reduce((acc, key) => acc?.[key], obj);

/** Recursively merges source into target (non-mutating). Arrays are replaced, not merged. */
export const deepMerge = (target: any, source: any): any => {
    const result = { ...target };
    for (const key of Object.keys(source)) {
        result[key] =
            source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])
                ? deepMerge(target[key] ?? {}, source[key])
                : source[key];
    }
    return result;
};

/** Builds a nested object from a dot-notation path and a leaf value.
 *  e.g. buildNestedObject('a.b.c', 42) → { a: { b: { c: 42 } } }
 */
export const buildNestedObject = (path: string, value: any): any =>
    path
        .split('.')
        .reduceRight((acc, key) => ({ [key]: acc }), value as any);

// ── Step navigation helpers ───────────────────────────────────────────────────

/**
 * Returns true if at least one field in the step has a value different
 * from the form's initial values.
 */
export const stepHasChanges = (
    fields: string[],
    values: FormikValues,
    initialValues: FormikValues
): boolean =>
    fields.some((field) => {
        const current = getNestedValue(values, field);
        const initial = getNestedValue(initialValues, field);
        return Array.isArray(current)
            ? JSON.stringify(current) !== JSON.stringify(initial)
            : current !== initial;
    });

/**
 * Returns true if none of the step's fields have a validation error.
 */
export const stepIsValid = (
    fields: string[],
    errors: FormikErrors<FormikValues>
): boolean =>
    fields.every((field) => !getNestedValue(errors, field));

/**
 * Builds the reset patch needed to revert a step's fields back to their
 * initial values, ready to be deep-merged into the current form values.
 */
export const buildStepResetPatch = (
    fields: string[],
    initialValues: FormikValues
): any =>
    fields.reduce((acc, field) => {
        const initialValue = getNestedValue(initialValues, field);
        return deepMerge(acc, buildNestedObject(field, initialValue));
    }, {} as any);

/**
 * Builds a touched patch that marks all of a step's fields as touched,
 * ready to be spread into formik.setTouched.
 */
export const buildStepTouchedPatch = (fields: string[]): Record<string, true> =>
    fields.reduce((acc, field) => ({ ...acc, [field]: true }), {});

// ── Submit guard ─────────────────────────────────────────────────────────────

/**
 * Returns true when the form has unsaved changes and passes validation —
 * i.e. the submit button should be enabled.
 */
export const canSubmitForm = (
    values: FormikValues,
    initialValues: FormikValues,
    isValid: boolean
): boolean =>
    JSON.stringify(initialValues) !== JSON.stringify(values) && isValid;